/* eslint-disable no-script-url */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from './CTASection.styles';
import { vehicleContext } from '../VehicleProvider';
import { MARKET } from '../../constants';
import { OaddEligibleDealer } from '../../hooks/useGetOaddDealers';

interface SecondButtonProps {
  weblink: string;
  isAvailableNow: boolean;
  isSticky?: boolean;
  secondButtonType: string;
  contactDealerUrl?: string;
  requestAQuoteUrl?: string;
  redirect?: string;
  onModalOpen: () => void;
  eDepositExceeded?: boolean;
  eligibleOaddDealers: OaddEligibleDealer[];
  eligibleDealerIds: string[];
}

const SecondButton: React.FC<SecondButtonProps> = ({
  weblink,
  isAvailableNow,
  isSticky,
  secondButtonType,
  contactDealerUrl,
  requestAQuoteUrl,
  redirect,
  onModalOpen,
  eDepositExceeded,
  eligibleOaddDealers,
  eligibleDealerIds,
}) => {
  const { t } = useTranslation();

  const { vehicle, market } = useContext(vehicleContext);

  const isMarketUSA = MARKET === 'USA';

  // add DDC traking url param to Tier III links
  const weblinkWithDDC = weblink?.includes('?')
    ? weblink?.concat('&ddcref=tier1_inventorysearch')
    : weblink?.concat('?ddcref=tier1_inventorysearch');

  const correctDealerListingUrl = useMemo(() => {
    if (!vehicle) return weblinkWithDDC;
    if (eligibleDealerIds.includes(vehicle?.kvpsSyncId)) {
      return eligibleOaddDealers.find((dealer) => dealer.dealerId === vehicle?.kvpsSyncId)?.rootUrl;
    }
    return weblinkWithDDC;
  }, [eligibleDealerIds, eligibleOaddDealers, vehicle, weblinkWithDDC]);

  const digitalLeadsData = JSON.stringify({
    vehicleType: vehicle?.vehicleType,
    market,
    presetForm: 'contact-dealer',
    dealerId: vehicle?.kvpsSyncId,
  });

  // CTA FUNCTIONAL SUMMARY for US and CAN market in README.md

  // CONTACT_DEALER button
  const contactDealerButton = (
    <CtaButton
      type="primary"
      href={isMarketUSA ? 'javascript:void(0)' : contactDealerUrl}
      sticky={isSticky}
      isAvailableNow={isAvailableNow}
      data-testid="contact-dealer-button"
      id="digital-leads"
      data-info={digitalLeadsData}
    >
      {t('vehiclePricingBar.dealerListPrice.contactDealer')}
    </CtaButton>
  );

  // REQUEST_A_QUOTE button
  const requestAQuoteButton = (
    <CtaButton
      type="primary"
      href={isMarketUSA ? 'javascript:void(0)' : requestAQuoteUrl}
      sticky={isSticky}
      data-testid="request-quote-button"
      id="digital-leads"
    >
      {t('vehiclePricingBar.dealerListPrice.contactDealer')}
    </CtaButton>
  );

  // VISIT_DEALER_LISTING button
  const visitDealerListingButton = (
    <CtaButton
      type="primary"
      href={correctDealerListingUrl}
      sticky={isSticky}
      data-testid="visit-dealer-website-button"
    >
      {t('vehiclePricingBar.dealerListPrice.visitDealerListing')}
    </CtaButton>
  );

  // PLACE_E_DEPOSIT button
  const placeEDepositButton = (
    <CtaButton
      type={eDepositExceeded ? 'disabled' : 'primary'}
      sticky={isSticky}
      onClick={() => {
        if (eDepositExceeded) return;
        onModalOpen();
      }}
      data-testid="e-deposit-button"
    >
      {t('vehiclePricingBar.dealerListPrice.eDeposit')}
      {t('vehiclePricingBar.dealerListPrice.hardcodedDepositAmount')}
    </CtaButton>
  );

  // RESERVED button
  // const reservedButton =
  //   redirect && status === 'ALLOCATED' ? (
  //     <Spinner />
  //   ) : (
  //     <CtaButton type="disabled" sticky={isSticky} data-testid="reserved-button">
  //       {t('vehiclePricingBar.dealerListPrice.reserved')}
  //     </CtaButton>
  //   );

  if (secondButtonType === 'CONTACT_DEALER') return contactDealerButton;

  if (secondButtonType === 'REQUEST_A_QUOTE') return requestAQuoteButton;

  if (secondButtonType === 'VISIT_DEALER_LISTING') return visitDealerListingButton;

  if (secondButtonType === 'PLACE_E_DEPOSIT') return placeEDepositButton;

  // if (secondButtonType === 'RESERVED') return reservedButton;

  return null;
};

export default SecondButton;
